import { BankRoutingScheme } from "./bankRoutingSchemes";

/**
 * Country reference data.
 *
 * The backend owns this — `GET /v3/country` serves it, and `useCountryConfig` merges the API row
 * over the bundled defaults below, field by field. The bundled values exist so nothing has a
 * loading state to handle: every consumer here is a label, a placeholder or a flag, all of which
 * have a safe answer before the request lands.
 *
 * The AU/US/NZ values are the ones this file has always shipped, so reading from the API is
 * behaviour-preserving by construction.
 *
 * Two things deliberately do NOT live here:
 *  - validation rules, which belong to the identifier *type* (see registrationValidators.ts) —
 *    Australia's ABN check is a weighted modulus, not something a column can express;
 *  - product decisions such as the print-supplier link (see productConfig.ts).
 */
/**
 * The countries Rideminder supports — decision of 2026-09-08: exactly these five. Typing
 * FALLBACK_COUNTRY_CONFIG against this set makes a missing bundled row (or a stray one) a compile
 * error, and the "supported set" test asserts each row is internally consistent. PH is not a
 * supported country and has no row.
 */
export const SUPPORTED_COUNTRY_CODES = ["AU", "NZ", "US", "GB", "CH"] as const;
export type SupportedCountryCode = (typeof SUPPORTED_COUNTRY_CODES)[number];

export type CountryConfig = {
  /** ISO 3166-1 alpha-2. */
  countryCode: string;
  name: string;
  currency: string;
  /** Pricing is always stored in kilometres; this only says how to present it. */
  distanceUnit: "km" | "mi";
  postalLabel: string;
  /** What this country calls a first-level subdivision: "State" in AU/US, "Region" in GB. */
  regionLabel: string;
  bankRoutingLabel: string;
  bankRoutingScheme: BankRoutingScheme;
  /** Business registration types this country accepts, in priority order; the first is default. */
  businessRegistrationTypes: string[];
  /** What this country calls its consumption tax. */
  taxLabel: string;
  /**
   * Driver and external-supplier tax identifier. Bound to the API's `region_au.abn` field, which is
   * used generically despite the AU-flavoured name. Not currently served by /v3/country, so these
   * four resolve from the bundled values below.
   */
  taxIdLabel: string;
  taxIdPlaceholder: string;
  taxIdInputType: "text" | "number";
  hasConsumptionTax: boolean;
  consumptionTaxLabel: string;
  /**
   * Stripe payment-method type for direct debit, where the country has one wired up.
   *
   * Absence is meaningful: it is what makes direct-debit setup unavailable rather than broken. GB
   * has no entry until Stripe Bacs is actually live, so a UK operator sees the existing "managed
   * through Stripe" panel instead of a form that cannot work.
   */
  stripeDebitPaymentMethod?: string;
  /**
   * The country has no direct-debit rail at all, so a provider's platform fees are collected from a
   * card kept on file. Distinct from `stripeDebitPaymentMethod` being merely absent-until-wired (GB):
   * this is the deliberate, permanent arrangement (CH — Stripe has no CHF direct debit).
   */
  platformFeeCard?: boolean;
  /** E.164 dialling code, e.g. "+41". Served by /v3/country. */
  phoneDialCode?: string;
  /** IANA zone the country defaults to, e.g. "Europe/Zurich". Served by /v3/country. */
  defaultTimezone?: string;
  /**
   * The country's Stripe publishable key, for `loadStripe`. Served by /v3/country and deliberately
   * NOT bundled below: it is environment-specific, and a hardcoded default here is exactly how five
   * templates ended up handing every country the Australian key. Undefined until the row lands, so
   * callers must wait on `isLoading` rather than treat absence as "no Stripe".
   *
   * Prefer this over the account-management keys endpoint when all you need is the key. That
   * endpoint also mints a verification PaymentIntent and a SetupIntent, both of which carry
   * `on_behalf_of` and so fail for a provider that has not connected Stripe (DEV-1694).
   */
  stripePublishableKey?: string;
};

/**
 * Used when the country is unknown.
 *
 * Deliberately NOT the Australian config. This file used to fall back to AU for anything it did not
 * recognise, which is the actual reason a UK driver was asked for an ABN — a wrong-but-confident
 * answer is worse than a plain one. Neutral labels are recoverable; a failing ABN checksum is not.
 */
export const NEUTRAL_COUNTRY_CONFIG: CountryConfig = {
  countryCode: "",
  name: "",
  currency: "",
  distanceUnit: "km",
  postalLabel: "Postal Code",
  regionLabel: "Region",
  bankRoutingLabel: "Bank Routing Number",
  bankRoutingScheme: "bsb",
  businessRegistrationTypes: [],
  taxLabel: "Tax",
  taxIdLabel: "Tax ID",
  taxIdPlaceholder: "",
  taxIdInputType: "text",
  hasConsumptionTax: false,
  consumptionTaxLabel: "",
  stripeDebitPaymentMethod: undefined,
  // Every merge-able field must appear here: resolveCountryConfig iterates NEUTRAL's keys.
  platformFeeCard: false,
  phoneDialCode: undefined,
  defaultTimezone: undefined,
  stripePublishableKey: undefined,
};

export const FALLBACK_COUNTRY_CONFIG: Record<SupportedCountryCode, CountryConfig> = {
  AU: {
    countryCode: "AU",
    name: "Australia",
    currency: "AUD",
    distanceUnit: "km",
    postalLabel: "Postcode",
    regionLabel: "State",
    bankRoutingLabel: "Bank State Branch",
    bankRoutingScheme: "bsb",
    businessRegistrationTypes: ["ABN", "ACN"],
    taxLabel: "GST",
    taxIdLabel: "ABN",
    taxIdPlaceholder: "00 000 000 000",
    taxIdInputType: "text",
    hasConsumptionTax: true,
    consumptionTaxLabel: "ABN Registered for GST",
    stripeDebitPaymentMethod: "au_becs_debit",
    phoneDialCode: "+61",
    defaultTimezone: "Australia/Sydney",
  },
  US: {
    countryCode: "US",
    name: "United States",
    currency: "USD",
    distanceUnit: "mi",
    postalLabel: "ZIP Code",
    regionLabel: "State",
    bankRoutingLabel: "Routing Number",
    bankRoutingScheme: "aba",
    businessRegistrationTypes: ["EIN"],
    taxLabel: "Sales Tax",
    taxIdLabel: "Tax ID (EIN/SSN)",
    taxIdPlaceholder: "123456789",
    taxIdInputType: "text",
    hasConsumptionTax: false,
    consumptionTaxLabel: "",
    stripeDebitPaymentMethod: "us_bank_account",
    phoneDialCode: "+1",
    defaultTimezone: "America/New_York",
  },
  GB: {
    countryCode: "GB",
    name: "United Kingdom",
    currency: "GBP",
    // The UK signs its roads in miles.
    distanceUnit: "mi",
    postalLabel: "Postcode",
    // Twelve ITL1 regions rather than states.
    regionLabel: "Region",
    bankRoutingLabel: "Sort Code",
    bankRoutingScheme: "sort_code",
    businessRegistrationTypes: ["CRN", "UTR", "VAT"],
    taxLabel: "VAT",
    taxIdLabel: "UTR",
    taxIdPlaceholder: "1234567890",
    taxIdInputType: "text",
    hasConsumptionTax: true,
    consumptionTaxLabel: "Registered for VAT",
    phoneDialCode: "+44",
    defaultTimezone: "Europe/London",
  },
  NZ: {
    countryCode: "NZ",
    name: "New Zealand",
    currency: "NZD",
    distanceUnit: "km",
    postalLabel: "Postcode",
    regionLabel: "Region",
    // A NZ bank account is one identifier (bank-branch-account-suffix); there is no BSB. The old
    // "Bank State Branch"/"bsb" here was Australian and acknowledged as wrong.
    bankRoutingLabel: "Bank Account Number",
    bankRoutingScheme: "nz_bank_account",
    businessRegistrationTypes: ["NZBN"],
    taxLabel: "GST",
    taxIdLabel: "IRD Number",
    taxIdPlaceholder: "000000000",
    taxIdInputType: "text",
    hasConsumptionTax: true,
    consumptionTaxLabel: "Registered for GST",
    // No stripeDebitPaymentMethod until the NZ BECS submit path exists in DebitsEditor; the backend
    // persists nz_becs_debit mandates already, so this is the only remaining gap.
    phoneDialCode: "+64",
    defaultTimezone: "Pacific/Auckland",
  },
  CH: {
    countryCode: "CH",
    name: "Switzerland",
    currency: "CHF",
    distanceUnit: "km",
    postalLabel: "Postcode",
    // Twenty-six cantons rather than states.
    regionLabel: "Canton",
    bankRoutingLabel: "IBAN",
    bankRoutingScheme: "iban",
    // The UID (CHE-123.456.789) is the one business identifier; the VAT number is the UID plus a
    // "MWST" suffix, which would overflow the backend's 16-character field, so it is not a type.
    businessRegistrationTypes: ["UID"],
    taxLabel: "VAT",
    taxIdLabel: "UID",
    taxIdPlaceholder: "CHE-123.456.789",
    taxIdInputType: "text",
    hasConsumptionTax: true,
    consumptionTaxLabel: "Registered for VAT",
    // Card-only: Stripe offers no CHF direct debit. Deliberately no stripeDebitPaymentMethod.
    platformFeeCard: true,
    phoneDialCode: "+41",
    defaultTimezone: "Europe/Zurich",
  },
};

/**
 * Merges an API row over the bundled defaults, one field at a time.
 *
 * Per-field rather than wholesale, so a backend that has not yet been migrated — or that adds a
 * field later — degrades to the bundled value for just that field instead of losing the row.
 *
 * Pure and exported so it can be tested without React.
 */
export const resolveCountryConfig = (apiRow: Partial<CountryConfig> | undefined, countryCode?: string): CountryConfig => {
  const code = (countryCode ?? apiRow?.countryCode ?? "").toUpperCase();
  // Indexed by an arbitrary string here on purpose: an API row for a country outside the bundled set
  // is still resolved (over NEUTRAL), it just has no bundled defaults.
  const bundled = (FALLBACK_COUNTRY_CONFIG as Partial<Record<string, CountryConfig>>)[code];

  if (!apiRow && !bundled) {
    if (code) {
      // eslint-disable-next-line no-console
      console.warn(`No country config for "${code}"; using neutral labels.`);
    }

    return { ...NEUTRAL_COUNTRY_CONFIG, countryCode: code };
  }

  const base = bundled ?? { ...NEUTRAL_COUNTRY_CONFIG, countryCode: code };

  if (!apiRow) return base;

  const merged = { ...base };
  (Object.keys(NEUTRAL_COUNTRY_CONFIG) as (keyof CountryConfig)[]).forEach((key) => {
    const value = apiRow[key];
    const isEmptyList = Array.isArray(value) && value.length === 0;

    if (value !== undefined && value !== null && value !== "" && !isEmptyList) {
      // The row is validated at the transform boundary; the cast keeps the merge generic.
      (merged[key] as unknown) = value;
    }
  });

  return merged;
};

/**
 * @deprecated Use the `useCountryConfig` hook, which layers the API over these defaults. Kept so
 * non-React callers and in-flight branches keep compiling.
 */
export const getCountryConfig = (country: string): CountryConfig => resolveCountryConfig(undefined, country);
